/**
 * MvbScorecardCard -- Home tile for the active MVB declaration (P7.4).
 *
 * Shows the active declaration's name + dual-signoff status +
 * IBS count + readiness + countdown to review_due_at.
 * Empty state when no active declaration exists.
 */
import { useTranslation } from "react-i18next";
import { InfoTip } from "shared/components/Explainer";
import { Link } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { BrassLink } from "shared/components/BrassLink";
import { Card, CardError } from "shared/components/Card";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import { useMvbScorecard } from "foundation/api/hooks";
import { membersSentence } from "shared/mvb/membersNow";

// Readiness ramp -> the rag.* status tones (cold = neutral up to live = green).
const READINESS_TONE: Record<string, PillTone> = {
  cold: "neutral",
  warm: "amber",
  hot: "amber",
  live: "green",
};

export function MvbScorecardCard() {
  const { t } = useTranslation();
  // Round 19 I24 -- it was a component literal.
  const MICRO = t("screens.mvbScorecardCard.micro");
  const query = useMvbScorecard();

  if (query.isLoading) {
    return (
      <Card testid="mvb-scorecard-card" data-state="loading" micro={MICRO} icon="mvb">
        <p className="mt-2 text-sm text-slate-600">
          {t("screens.mvbScorecardCard.loading")}
        </p>
      </Card>
    );
  }

  // Round 18 K27 -- "none in force" is a 200 now; a 404 is an older
  // backend saying the same thing.
  const noneInForce = query.data?.in_force === false || query.error?.status === 404;
  if (query.error || noneInForce) {
    if (noneInForce) {
      return (
        <Card testid="mvb-scorecard-card" data-state="empty" micro={MICRO} icon="mvb">
          <p className="mt-2 text-sm text-slate-600">
            {t("screens.mvbScorecardCard.noActiveMvbDeclarationDriveOneThrough")}
          </p>
          <BrassLink to="/foundation/mvb" className="mt-3 inline-block text-xs">
            {t("screens.mvbScorecardCard.openMvbRegister")}
          </BrassLink>
        </Card>
      );
    }
    return (
      <CardError
        testid="mvb-scorecard-card"
        micro={MICRO}
        icon="mvb"
        message={t("screens.mvbScorecardCard.couldnTLoadTheMvb")}
      />
    );
  }

  const score = query.data;
  if (!score || score.in_force === false) return null;

  const reviewIn = score.review_due_in_days;
  const reviewClass =
    reviewIn !== null && reviewIn < 0
      ? "text-rag-red font-medium"
      : reviewIn !== null && reviewIn < 30
        ? "text-rag-amber font-medium"
        : "text-slate-600";
  // Round 20 -- a declaration past its review read "-40 days".
  const reviewWords =
    reviewIn === null
      ? "—"
      : reviewIn < 0
        ? t("screens.mvbScorecardCard.reviewOverdueBy", { count: -reviewIn })
        : reviewIn === 0
          ? t("screens.mvbScorecardCard.reviewToday")
          : t("screens.mvbScorecardCard.reviewInDays", { count: reviewIn });

  const membersLine = membersSentence(score.members_now, t);

  return (
    <Card
      testid="mvb-scorecard-card"
      data-state="active"
      data-declaration-id={score.declaration_id}
      className="space-y-2"
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.16em] text-slate-400">
          <MarsadIcon name="mvb" size={13} className="text-brand-brassDark" />
          {MICRO}
          {/* F2: "MVB DECLARATION ... CRO checked, AE checked,
              DUAL-SIGNED" was met cold by four personas on the home
              page. Two constructs, two tips -- the set, and what the
              two signatures mean. */}
          <InfoTip termKey="mvb" />
          <InfoTip termKey="dual_signoff" />
        </p>
        <StatusPill tone="neutral" mono>
          {t("screens.mvbScorecardCard.active")}
        </StatusPill>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <Link
          to={`/foundation/mvb/${score.declaration_id}`}
          className="block font-display text-xl font-semibold text-ink hover:text-brand-brassDark"
        >
          {score.declaration_name}
        </Link>
        {score.readiness_state && (
          <StatusPill
            testid="mvb-readiness-pill"
            tone={READINESS_TONE[score.readiness_state] ?? "neutral"}
          >
            {score.readiness_state}
          </StatusPill>
        )}
      </div>
      {/* P214.6 (Observatory): definition list -- muted dt labels, mono
          end-aligned dd values. */}
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-[10px] text-[12.5px] pt-1">
        {/* Round 18 L10 -- a set holding a function or candidates says so;
            an all-admitted set reads "IBSes in MVB set 6" as before. */}
        {membersLine ? (
          <>
            <dt className="text-ink-muted">{t("screens.mvbScorecardCard.inMvbSet")}</dt>
            <dd
              className="text-ink text-end"
              data-testid="mvb-members-sentence"
              data-count={score.ibs_count}
            >
              {membersLine}
            </dd>
          </>
        ) : (
          <>
            <dt className="text-ink-muted">
              {t("screens.mvbScorecardCard.ibsesInMvbSet")}
            </dt>
            <dd className="text-ink font-mono text-end" data-testid="mvb-ibs-count">
              {score.ibs_count}
            </dd>
          </>
        )}
        <dt className="text-ink-muted">
          {t("screens.mvbScorecardCard.effectiveFrom")}
        </dt>
        <dd className="text-ink font-mono text-end">{score.effective_from ?? "—"}</dd>
        <dt className="text-ink-muted">
          {t("screens.mvbScorecardCard.gate1Threshold")}
          <InfoTip termKey="gate1_threshold" />
        </dt>
        <dd className="text-ink font-mono text-end">
          {score.mvb_threshold_hours}
          {t("screens.mvbScorecardCard.h")}
        </dd>
        <dt className="text-ink-muted">{t("screens.mvbScorecardCard.reviewIn")}</dt>
        <dd
          className={`font-mono text-end ${reviewClass}`}
          data-testid="mvb-review-due"
        >
          {reviewWords}
        </dd>
      </dl>
      {/* Signature row -- top-ruled, CRO/AE acknowledgement. */}
      <div className="flex items-center gap-3 border-t border-paper-2 pt-[14px] text-xs text-ink-2">
        <span>
          {t("screens.mvbScorecardCard.cro")}{" "}
          <span className={score.cro_signed_at ? "font-semibold text-rag-green" : ""}>
            {score.cro_signed_at ? "✓" : "—"}
          </span>{" "}
          {t("screens.mvbScorecardCard.ae")}{" "}
          <span className={score.ae_signed_at ? "font-semibold text-rag-green" : ""}>
            {score.ae_signed_at ? "✓" : "—"}
          </span>
        </span>
        {score.dual_signoff_complete && (
          <StatusPill testid="mvb-dual-signed" tone="green" mono>
            {t("screens.mvbScorecardCard.dualSigned")}
          </StatusPill>
        )}
      </div>
    </Card>
  );
}
